import _ from 'lodash'
import type {DalPointers} from '../types'
import type {
    Breakpoint,
    BreakpointId,
    CompRef,
    DalValue,
    DeserializationMappers,
    Pointer,
    VariantRelation,
    VariantsReplacementOperation
} from '@wix/document-services-types'
import {createReferredId, refCompDelimiter} from './inflationUtils'
import {getIdFromRef} from './dataUtils'
import {DATA_TYPES, RELATION_DATA_TYPES, VARIANTS} from '../constants/constants'
import {stripHashIfExists} from './refArrayUtils'
import type {DataExtensionAPI} from '../extensions/data'
import type {ExtensionAPI} from '@wix/document-manager-core'
import {DEFAULT_BREAKPOINTS_MAX_RANGE} from '../extensions/import-export/json/constants'
import type {DataModelExtensionAPI} from '../extensions/dataModel/dataModel'
import type {SchemaExtensionAPI} from '../extensions/schema/schema'
type PossibleMatchingBreakpoints = Record<BreakpointId, Breakpoint & {id: BreakpointId}>

interface RelationData {
    originalVariants: Set<string>
    transformedVariants: Set<string>
    relationPointer?: Pointer
    from?: string
    to?: string
    value?: DalValue
    indexInRefArray?: number
}

interface ScopedValueToMaintain {
    variants: string[]
    value: DalValue
    indexInRefArray: number
}
/**
 * get variant predicate by variantType according to variants data schemas
 * f.i variantType = 'Hover' , options = {compPointer: 'compId'}, matches Hover required fields in variantsSchemas.js
 * @param variantType
 * @param options
 * @returns {*} predicate by variant type
 */
const getDefaultVariantDataByVariantType = (
    variantType: string,
    {compPointer, values}: {compPointer: Pointer; values: string[]}
): {componentId: string; type: string; values?: string[]; name?: string; trigger?: string} | undefined => {
    switch (variantType) {
        case VARIANTS.VARIANT_TYPES.MOBILE:
        case VARIANTS.VARIANT_TYPES.HOVER:
        case VARIANTS.VARIANT_TYPES.PRESET:
            return {componentId: compPointer.id, type: variantType}
        case VARIANTS.VARIANT_TYPES.SELECTED:
            return {componentId: compPointer.id, type: variantType}
        case VARIANTS.VARIANT_TYPES.REPEATER_PATTERN:
            return {componentId: compPointer.id, type: variantType}
        case VARIANTS.VARIANT_TYPES.STATE:
            return {componentId: compPointer.id, type: variantType, name: ''}
        case VARIANTS.VARIANT_TYPES.TRIGGER:
            return {componentId: compPointer.id, type: variantType, trigger: ''}
        case VARIANTS.VARIANT_TYPES.REPEATER_PATTERN_LIST:
        case VARIANTS.VARIANT_TYPES.VARIANTS_LIST:
        case VARIANTS.VARIANT_TYPES.BREAKPOINTS_DATA:
            return {componentId: compPointer.id, type: variantType, values: values || []}
    }
}

const hasActiveVariants = ({dal, pointers}: DalPointers, componentId: string) => {
    const activeVariantPointer = pointers.activeVariants.getActiveVariant(componentId)
    return !_.isEmpty(dal.get(activeVariantPointer))
}

const isLocalVariant = (variant: string | Pointer, separateRemoteVariants?: boolean) => {
    if (!separateRemoteVariants) {
        return true
    }
    const id = _.isString(variant) ? variant : variant.id
    return !id.includes(refCompDelimiter)
}

const transformToRelationData = (relation: VariantRelation) => {
    const relationVariants = (relation.variants || []).map(variant => stripHashIfExists(variant))

    return {
        to: relation.to,
        from: relation.from,
        originalVariants: new Set(relationVariants),
        transformedVariants: new Set(relationVariants)
    }
}
const sortRelationsBySpecificity = (
    {originalVariants: originalVariants1}: any,
    {originalVariants: originalVariants2}: any
) => originalVariants1.size - originalVariants2.size

const applyOperations = (operations: VariantsReplacementOperation[], relationsData: RelationData[]) => {
    for (const operation of operations) {
        const {from, to} = operation

        for (const {originalVariants, transformedVariants} of relationsData) {
            const shouldApplyOperation = from.every(variant => originalVariants.has(variant.id))

            if (shouldApplyOperation) {
                for (const variant of from) {
                    transformedVariants.delete(variant.id)
                }

                for (const variant of to) {
                    transformedVariants.add(variant.id)
                }
            }
        }
    }
}

const getValidRelations = (
    relationsData: RelationData[],
    isValidRelationPredicate: (variants: string[]) => boolean = () => true,
    relationsPointersToRemove: Pointer[] = []
) => {
    const validRelations = {}
    let relationToDefault

    for (const {relationPointer, indexInRefArray, from, to, originalVariants, transformedVariants} of relationsData) {
        const variants = [...transformedVariants]

        if (transformedVariants.size === 0) {
            relationToDefault = {relationPointer, to}
            relationsPointersToRemove.push(relationPointer as Pointer)
            continue
        }

        if (!isValidRelationPredicate(variants)) {
            relationsPointersToRemove.push(relationPointer as Pointer)
            continue
        }

        const relationKey = variants.sort().join(',')

        if (validRelations[relationKey]) {
            relationsPointersToRemove.push(validRelations[relationKey].relationPointer)
        }

        validRelations[relationKey] = {
            ...(relationPointer && {relationPointer}),
            ...(indexInRefArray && {indexInRefArray}),
            from,
            to,
            originalVariants,
            transformedVariants
        }
    }

    return {
        validRelations,
        relationToDefault,
        relationsPointersToRemove
    }
}
const sortRelationsByIndexInRefArray = (
    relationData1: RelationData | ScopedValueToMaintain,
    relationData2: RelationData | ScopedValueToMaintain
    // @ts-ignore
) => relationData1.indexInRefArray - relationData2.indexInRefArray

const createVariantRelation = (variants: string[], from: string, to: string | DalValue, id?: string) => ({
    ...(id && {id}),
    type: RELATION_DATA_TYPES.VARIANTS,
    variants: variants.map(variant => (typeof variant === 'string' ? `#${getIdFromRef(variant)}` : variant)),
    from: `#${getIdFromRef(from)}`,
    to: typeof to === 'string' ? `#${getIdFromRef(to)}` : to
})

const replaceVariantsOnSerializedOverrides = (
    sourceRefArray: VariantRelation[],
    operations: VariantsReplacementOperation[] = []
) => {
    const relationsData: RelationData[] = []

    if (!Array.isArray(sourceRefArray) || !Array.isArray(operations) || operations.length === 0) {
        return sourceRefArray
    }

    for (const [indexInRefArray, refArrayValue] of sourceRefArray.entries()) {
        relationsData.push({indexInRefArray, ...transformToRelationData(refArrayValue)})
    }

    relationsData.sort(sortRelationsBySpecificity)
    applyOperations(operations, relationsData)

    const {validRelations, relationToDefault} = getValidRelations(relationsData)
    const refArray = (Object.values(validRelations) as RelationData[])
        .sort(sortRelationsByIndexInRefArray)
        .map(({from, to, transformedVariants}: RelationData) =>
            createVariantRelation([...transformedVariants], from as string, to)
        )

    if (relationToDefault?.to) {
        refArray.unshift(relationToDefault.to as any)
    }

    return refArray
}

const updateVariantRelations = (
    extensionAPI: ExtensionAPI,
    compToAddPointer: Pointer,
    dataItem: Record<string, any>,
    mappers: DeserializationMappers
) => {
    const {variantMapper, oldToNewIdMap} = mappers ?? {}
    const refArrayValuesReducer = (cleanRefArray: any, refArrayValue: any) => {
        if (refArrayValue.type === RELATION_DATA_TYPES.VARIANTS) {
            const [, ...referredStructureIds] = refArrayValue.from.split(refCompDelimiter)

            const updatedVariants = refArrayValue.variants.map(
                (oldVariant: any) => oldToNewIdMap[getIdFromRef(oldVariant)]
            )
            const newVariants = updatedVariants.filter(Boolean)

            if (newVariants.length !== refArrayValue.variants.length) {
                return cleanRefArray
            }

            refArrayValue = (extensionAPI as DataExtensionAPI).data.variantRelation.create(
                newVariants,
                createReferredId(compToAddPointer.id, referredStructureIds),
                refArrayValue.to
            )
        }

        cleanRefArray.push(refArrayValue)

        return cleanRefArray
    }
    dataItem.values = replaceVariantsOnSerializedOverrides(dataItem.values, variantMapper)
    dataItem.values = oldToNewIdMap ? dataItem.values.reduce(refArrayValuesReducer, []) : dataItem.values
}

const getBreakpointsIntersectionArea = (breakpoint1: Breakpoint, breakpoint2: Breakpoint): number | undefined => {
    const intersectionMin = _.max([breakpoint1.min, breakpoint2.min, 0])!
    const intersectionMax = _.min([breakpoint1.max, breakpoint2.max, DEFAULT_BREAKPOINTS_MAX_RANGE])!
    const intersectionArea = intersectionMax - intersectionMin

    if (intersectionArea > 0) {
        return intersectionArea
    }
}

const getBestMatchingBreakpoint = (
    breakpoint: Breakpoint,
    possibleMatchingBreakpoints: PossibleMatchingBreakpoints
): BreakpointId | undefined =>
    _(possibleMatchingBreakpoints)
        .values()
        .maxBy(possibleMatch => getBreakpointsIntersectionArea(possibleMatch, breakpoint))?.id

const createDeserializedVariantsList = (
    extensionAPI: ExtensionAPI,
    futureCompPointer: Pointer,
    componentPageId: string,
    variantsList: {values: string[]}
): string => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const variantsListId = dataModel.generateNewId(DATA_TYPES.variants)
    const variantListType = VARIANTS.VARIANT_TYPES.VARIANTS_LIST
    const values = variantsList?.values
    const data = getDefaultVariantDataByVariantType(variantListType, {compPointer: futureCompPointer, values})
    schemaAPI.addDefaultsAndValidate(variantListType, data, DATA_TYPES.variants)
    return dataModel.addDeserializedItem(data, DATA_TYPES.variants, componentPageId, variantsListId).id
}

const removeVariantsFromPointer = (pointer: Pointer): Pointer => _.omit(pointer, 'variants')

function normalizeVariant(variant: Pointer | string): Pointer {
    if (_.isString(variant)) {
        return {id: stripHashIfExists(variant), type: DATA_TYPES.variants}
    }
    return variant
}

function normalizeVariants(pointerVariants: (Pointer | string)[] | undefined): Pointer[] | undefined {
    if (!pointerVariants) {
        // An undefined variants array is NOT the same as an empty variants array, it should be preserved
        return undefined
    }
    return pointerVariants.map(normalizeVariant)
}

function normalizeCompRefVariants(compRef: CompRef): CompRef {
    return {
        ...compRef,
        variants: normalizeVariants(compRef.variants)
    }
}

export {
    hasActiveVariants,
    isLocalVariant,
    replaceVariantsOnSerializedOverrides,
    applyOperations,
    updateVariantRelations,
    getBestMatchingBreakpoint,
    getBreakpointsIntersectionArea,
    createDeserializedVariantsList,
    getDefaultVariantDataByVariantType,
    removeVariantsFromPointer,
    normalizeCompRefVariants
}
